import { Dispatch, SetStateAction, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { AddUserGroup, Group, Page, User } from '../../../../../types';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import DeleteUserFromGroupDialog from './delete-user-from-group';
import AddToGroupDialog from './bulk-add';
import CompareGroupsDialog from './compare-groups';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import AddToGroupDialogIndexed from './bulk-add-indexed';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CompareArrows from '@mui/icons-material/CompareArrows';
import Groups from '@mui/icons-material/Groups';
import Logout from '@mui/icons-material/Logout';

interface UserGroupsProps {
  user: User | undefined;
  userEmail: string;
  userRoles?: string[];
  groups: Page<Group[]> | undefined;
  groupsLoading: boolean;
  groupsFetching: boolean;
  paginationModel: {
    cursor: string | null;
    results: Group[];
    nameSpace: string;
    firstPull: boolean;
    searchTime: number;
    pageSize: number;
  };
  setPaginationModel: Dispatch<
    SetStateAction<{
      cursor: string | null;
      results: Group[];
      nameSpace: string;
      firstPull: boolean;
      searchTime: number;
      pageSize: number;
    }>
  >;
}

export default function UserGroups({
  userEmail,
  user,
  userRoles,
  groups,
  groupsLoading,
  groupsFetching,
  paginationModel,
  setPaginationModel,
}: UserGroupsProps) {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [openDeleteUserFromGroup, setOpenDeleteUserFromGroup] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<AddUserGroup[]>([]);
  const [openAddToGroup, setOpenAddToGroup] = useState(false);
  const [openCompareGroups, setOpenCompareGroups] = useState(false);
  const [comparedUser, setComparedUser] = useState('');
  const { currentTier, features } = useUserSettings();
  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Group Email',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Group Name',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
    },
  ];

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddToGroup(true)}
            disabled={
              !isAbleToSeeUIElement(userRoles!, UserRoles.AddGroupMembers.value)
            }
            label="Add Group"
          />
        }
        secondaryButtons={
          <>
            {' '}
            <Tooltip arrow title="Compare">
              <span>
                <IconButton
                  onClick={() => setOpenCompareGroups(true)}
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles!,
                      UserRoles.AddGroupMembers.value
                    )
                  }
                >
                  <CompareArrows />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip arrow title="Leave Group">
              <span>
                <IconButton
                  onClick={() => setOpenDeleteUserFromGroup(true)}
                  disabled={
                    selectedRows.length === 0 ||
                    !isAbleToSeeUIElement(
                      userRoles!,
                      UserRoles.RemoveGroupMembers.value
                    )
                  }
                >
                  <Logout
                    sx={{
                      transform: 'rotate(180deg)',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.UserGroups, paginationModel],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      ></SharedToolbar>
    );
  }

  const sortedGroups = groups
    ? [...groups.results.sort((a, b) => a.email.localeCompare(b.email))]
    : [];

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Groups', <Groups />),
        }}
        getRowId={(row) => row.email}
        rows={sortedGroups}
        columns={columns}
        onPaginationModelChange={(newModel) => {
          if (newModel.page !== currentPage) {
            setCurrentPage(newModel.page);
          }
          setPaginationModel({
            ...paginationModel,
            firstPull: newModel.page === 0,
            cursor: groups?.cursor || null,
            pageSize: newModel.pageSize,
          });
        }}
        loading={groupsLoading || groupsFetching}
        disableColumnSelector
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      {openDeleteUserFromGroup && user ? (
        <DeleteUserFromGroupDialog
          open={openDeleteUserFromGroup}
          handleClose={() => setOpenDeleteUserFromGroup(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          userEmail={userEmail}
          user={user}
        />
      ) : null}
      {openAddToGroup ? (
        currentTier === 'enterprise' &&
        isFeatureEnabled('indexGroupsReact', features) ? (
          <AddToGroupDialogIndexed
            open={openAddToGroup}
            handleClose={() => {
              setSelectedValues([]);
              setOpenAddToGroup(false);
            }}
            userEmail={userEmail}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            currentUserGroups={groups}
          />
        ) : (
          <AddToGroupDialog
            open={openAddToGroup}
            handleClose={() => {
              setSelectedValues([]);
              setOpenAddToGroup(false);
            }}
            userEmail={userEmail}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            currentUserGroups={groups}
          />
        )
      ) : null}
      {openCompareGroups ? (
        <CompareGroupsDialog
          open={openCompareGroups}
          handleClose={() => {
            queryClient.removeQueries({
              queryKey: [UserKeys.UserGroups, comparedUser],
            });
            setComparedUser('');
            setOpenCompareGroups(false);
          }}
          userEmail={userEmail}
          data={groups}
          comparedUser={comparedUser}
          setComparedUser={setComparedUser}
        />
      ) : null}
    </>
  );
}
